import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { MaintenanceRecord } from '../../../../../types/api';
import {
  formatDate,
  getMaintenanceTypeDisplayName,
  preloadMaintenanceTypeDisplayNames,
} from '../../../../../utils/maintenanceTypeUtils';
import { useTranslation } from '../../../../../i18n';

const StyledHistoricalCard = styled(Box)({
  padding: '12px',
  marginBottom: '8px',
  border: '1px solid #e0e0e0',
  borderRadius: '8px',
  backgroundColor: '#fafafa',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: '#f5f5f5',
  },
});

interface HistoricalMaintenanceCardProps {
  record: MaintenanceRecord;
  onClick?: (record: MaintenanceRecord) => void;
}

export const HistoricalMaintenanceCard: React.FC<HistoricalMaintenanceCardProps> = ({
  record,
  onClick,
}) => {
  const { t } = useTranslation();
  const [displayName, setDisplayName] = useState<string>('');

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onClick?.(record);
  };

  // Use end_date for display, fall back to start_date
  const displayDate = record.end_date || record.start_date;
  const isMultiDay = record.start_date !== record.end_date && record.end_date;

  // Load display name for maintenance type
  useEffect(() => {
    const loadDisplayName = async () => {
      await preloadMaintenanceTypeDisplayNames();
      const name = await getMaintenanceTypeDisplayName(record.maintenance_type);
      setDisplayName(name);
    };

    if (record.maintenance_type) {
      loadDisplayName();
    }
  }, [record.maintenance_type]);

  return (
    <StyledHistoricalCard onClick={handleClick}>
      <Box
        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#333' }}>
          {displayName || record.maintenance_type}
        </Typography>
        <Typography variant="caption" sx={{ color: '#666' }}>
          {isMultiDay
            ? `${formatDate(record.start_date)} - ${formatDate(record.end_date)}`
            : formatDate(displayDate)}
        </Typography>
      </Box>

      {/* Work Description (if available) */}
      {record.work_description && (
        <Typography variant="body2" sx={{ color: '#555', mb: 1, fontSize: '0.85rem' }}>
          {record.work_description}
        </Typography>
      )}

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
          <Typography variant="caption" sx={{ color: '#666' }}>
            {t('maintenanceTab.performedBy')}{' '}
            {record.performed_by_name || record.performed_by || t('maintenanceTab.unknown')}
          </Typography>
          {record.quality_checked && record.quality_checked_by_name && (
            <Typography variant="caption" sx={{ color: '#666', fontSize: '0.7rem' }}>
              {t('maintenanceTab.reviewedBy')} {record.quality_checked_by_name}
            </Typography>
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {record.quality_checked && (
            <Typography
              variant="caption"
              sx={{
                color: '#2196f3',
                fontWeight: 500,
                backgroundColor: '#e3f2fd',
                padding: '2px 6px',
                borderRadius: '10px',
                fontSize: '0.7rem',
              }}
            >
              {t('maintenanceTab.qualityChecked')}
            </Typography>
          )}
          <Typography
            variant="caption"
            sx={{
              color: '#4caf50',
              fontWeight: 500,
              backgroundColor: '#e8f5e8',
              padding: '2px 8px',
              borderRadius: '12px',
            }}
          >
            {record.status}
          </Typography>
        </Box>
      </Box>
    </StyledHistoricalCard>
  );
};
